/**
 * LauraInsightsPanel - Displays AI-generated insights from Laura
 *
 * Reusable component that shows Laura's analysis with:
 * - Executive summary
 * - Health score indicator
 * - Key insights (success/warning/opportunity)
 * - Actionable recommendations
 */

import React from 'react';
import './LauraInsightsPanel.css';

interface KeyInsight {
  insight: string;
  type: 'success' | 'warning' | 'opportunity';
  metric?: string;
  value?: string;
}

interface Recommendation {
  action: string;
  priority: 'high' | 'medium' | 'low';
  expected_impact: string;
}

interface Trends {
  member_growth?: 'up' | 'down' | 'stable';
  revenue?: 'up' | 'down' | 'stable';
  engagement?: 'up' | 'down' | 'stable';
}

interface LauraAnalysis {
  executive_summary?: string;
  health_score?: number;
  key_insights?: KeyInsight[];
  recommendations?: Recommendation[];
  trends?: Trends;
  ai_generated?: boolean;
  // For store analysis
  performance_summary?: string;
  performance_rating?: 'excellent' | 'good' | 'average' | 'needs_attention';
  member_insights?: Array<{ insight: string; type: string; action: string }>;
  growth_opportunities?: Array<{ opportunity: string; effort: string; impact: string }>;
  // For member analysis
  member_profile?: string;
  value_tier?: 'champion' | 'loyal' | 'growing' | 'at_risk' | 'dormant';
  purchase_patterns?: Array<{ pattern: string; implication: string }>;
  engagement_suggestions?: Array<{ suggestion: string; timing: string; expected_result: string }>;
}

interface Props {
  analysis: LauraAnalysis | null;
  loading?: boolean;
  compact?: boolean;
  title?: string;
}

const LauraInsightsPanel: React.FC<Props> = ({
  analysis,
  loading = false,
  compact = false,
  title = "Laura's Analysis"
}) => {
  if (loading) {
    return (
      <div className={`laura-insights-panel ${compact ? 'compact' : ''}`}>
        <div className="panel-header">
          <div className="laura-avatar">
            <span>L</span>
          </div>
          <div className="header-text">
            <h3>{title}</h3>
            <span className="analyzing">Analyzing data...</span>
          </div>
        </div>
        <div className="loading-skeleton">
          <div className="skeleton-line long" />
          <div className="skeleton-line medium" />
          <div className="skeleton-line short" />
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className={`laura-insights-panel ${compact ? 'compact' : ''}`}>
        <div className="panel-header">
          <div className="laura-avatar">
            <span>L</span>
          </div>
          <div className="header-text">
            <h3>{title}</h3>
            <span className="subtitle">No analysis available</span>
          </div>
        </div>
      </div>
    );
  }

  const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return <span className="trend-icon up">+</span>;
      case 'down': return <span className="trend-icon down">-</span>;
      default: return <span className="trend-icon stable">=</span>;
    }
  };

  const getHealthScoreColor = (score?: number) => {
    if (!score) return '#6b7280';
    if (score >= 80) return '#059669';
    if (score >= 60) return '#f59e0b';
    return '#dc2626';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      default: return '#3b82f6';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'success': return '✓';
      case 'warning': return '!';
      case 'opportunity': return '★';
      default: return '•';
    }
  };

  const getTierLabel = (tier?: string) => {
    switch (tier) {
      case 'champion': return { label: 'Champion', color: '#059669' };
      case 'loyal': return { label: 'Loyal', color: '#3b82f6' };
      case 'growing': return { label: 'Growing', color: '#8b5cf6' };
      case 'at_risk': return { label: 'At Risk', color: '#f59e0b' };
      case 'dormant': return { label: 'Dormant', color: '#6b7280' };
      default: return { label: tier || 'Unknown', color: '#6b7280' };
    }
  };

  const getRatingLabel = (rating?: string) => {
    switch (rating) {
      case 'excellent': return { label: 'Excellent', color: '#059669' };
      case 'good': return { label: 'Good', color: '#3b82f6' };
      case 'average': return { label: 'Average', color: '#f59e0b' };
      case 'needs_attention': return { label: 'Needs Attention', color: '#dc2626' };
      default: return { label: rating || 'Unknown', color: '#6b7280' };
    }
  };

  return (
    <div className={`laura-insights-panel ${compact ? 'compact' : ''}`}>
      <div className="panel-header">
        <div className="laura-avatar">
          <span>L</span>
        </div>
        <div className="header-text">
          <h3>{title}</h3>
          {analysis.ai_generated !== false && (
            <span className="ai-badge">AI-Powered</span>
          )}
        </div>
        {analysis.health_score !== undefined && (
          <div
            className="health-score"
            style={{ borderColor: getHealthScoreColor(analysis.health_score) }}
          >
            <span
              className="score-value"
              style={{ color: getHealthScoreColor(analysis.health_score) }}
            >
              {analysis.health_score}
            </span>
            <span className="score-label">Health</span>
          </div>
        )}
        {analysis.value_tier && (
          <div
            className="tier-badge"
            style={{ backgroundColor: getTierLabel(analysis.value_tier).color }}
          >
            {getTierLabel(analysis.value_tier).label}
          </div>
        )}
        {analysis.performance_rating && (
          <div
            className="rating-badge"
            style={{ backgroundColor: getRatingLabel(analysis.performance_rating).color }}
          >
            {getRatingLabel(analysis.performance_rating).label}
          </div>
        )}
      </div>

      {/* Summary Section */}
      {(analysis.executive_summary || analysis.performance_summary || analysis.member_profile) && (
        <div className="summary-section">
          <p className="summary-text">
            {analysis.executive_summary || analysis.performance_summary || analysis.member_profile}
          </p>
        </div>
      )}

      {/* Trends (Network) */}
      {analysis.trends && !compact && (
        <div className="trends-row">
          {analysis.trends.member_growth && (
            <div className="trend-item">
              {getTrendIcon(analysis.trends.member_growth)}
              <span>Members</span>
            </div>
          )}
          {analysis.trends.revenue && (
            <div className="trend-item">
              {getTrendIcon(analysis.trends.revenue)}
              <span>Revenue</span>
            </div>
          )}
          {analysis.trends.engagement && (
            <div className="trend-item">
              {getTrendIcon(analysis.trends.engagement)}
              <span>Engagement</span>
            </div>
          )}
        </div>
      )}

      {/* Key Insights (Network) */}
      {analysis.key_insights && analysis.key_insights.length > 0 && !compact && (
        <div className="insights-section">
          <h4>Key Insights</h4>
          <div className="insights-list">
            {analysis.key_insights.map((insight, index) => (
              <div key={index} className={`insight-item ${insight.type}`}>
                <span className="insight-icon">{getInsightIcon(insight.type)}</span>
                <span className="insight-text">{insight.insight}</span>
                {insight.value && (
                  <span className="insight-value">{insight.value}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Member Insights (Store) */}
      {analysis.member_insights && analysis.member_insights.length > 0 && !compact && (
        <div className="insights-section">
          <h4>Member Insights</h4>
          <div className="insights-list">
            {analysis.member_insights.map((insight, index) => (
              <div key={index} className="insight-item">
                <span className="insight-text">{insight.insight}</span>
                {insight.action && (
                  <span className="insight-action">{insight.action}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Purchase Patterns (Member) */}
      {analysis.purchase_patterns && analysis.purchase_patterns.length > 0 && !compact && (
        <div className="patterns-section">
          <h4>Purchase Patterns</h4>
          <div className="patterns-list">
            {analysis.purchase_patterns.map((pattern, index) => (
              <div key={index} className="pattern-item">
                <span className="pattern-text">{pattern.pattern}</span>
                <span className="pattern-implication">{pattern.implication}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations (Network) */}
      {analysis.recommendations && analysis.recommendations.length > 0 && !compact && (
        <div className="recommendations-section">
          <h4>Recommendations</h4>
          <div className="recommendations-list">
            {analysis.recommendations.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <span
                  className="priority-dot"
                  style={{ backgroundColor: getPriorityColor(rec.priority) }}
                />
                <div className="rec-content">
                  <span className="rec-action">{rec.action}</span>
                  <span className="rec-impact">{rec.expected_impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Growth Opportunities (Store) */}
      {analysis.growth_opportunities && analysis.growth_opportunities.length > 0 && !compact && (
        <div className="opportunities-section">
          <h4>Growth Opportunities</h4>
          <div className="opportunities-list">
            {analysis.growth_opportunities.map((opp, index) => (
              <div key={index} className="opportunity-item">
                <span className="opp-text">{opp.opportunity}</span>
                <div className="opp-meta">
                  <span className={`effort effort-${opp.effort}`}>{opp.effort} effort</span>
                  <span className={`impact impact-${opp.impact}`}>{opp.impact} impact</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Engagement Suggestions (Member) */}
      {analysis.engagement_suggestions && analysis.engagement_suggestions.length > 0 && !compact && (
        <div className="suggestions-section">
          <h4>Engagement Suggestions</h4>
          <div className="suggestions-list">
            {analysis.engagement_suggestions.map((sug, index) => (
              <div key={index} className="suggestion-item">
                <span className="sug-text">{sug.suggestion}</span>
                {sug.timing && <span className="sug-timing">{sug.timing}</span>}
                {sug.expected_result && (
                  <span className="sug-result">{sug.expected_result}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LauraInsightsPanel;
